<template>
  <div>
    <!-- Skeleton for reservation summary -->
    <ReservationsSkeleton v-if="isServicesLoading" />

    <!-- Actual content shown when loading is finished -->
    <div v-else class="reservation-summary mt-custom p-2 p-md-4 bg-blue-100">
      <h3 class="title-4 mb-3">
        {{ t('reservations.summaryTitle') }}
      </h3>
      <OptionList :options="optionList" />
      <!-- Price and Reserve Button Section -->
      <PriceAndQuoteButton
        v-if="showQuoteOrCartBlock"
        :booking="booking"
        :buttonText="t('reservations.reserve')"
        :hide-action-button="hideActionButton"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, computed, PropType } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useStore } from 'vuex' // 1) Import useStore
  import { useServiceCategorization } from '@/composables/useServiceCategorization'

  // Types
  import { BookingSummary } from '@/services/BookingService.interface'
  import { BookingStatus } from '@/config'

  // Icons
  import AccommodationIcon from '@/assets/AccommondationIcon.vue'
  import SkipassIcon from '@/assets/SkipassIcon.vue'
  import EquipmentIcon from '@/assets/EquipmentIcon.vue'
  import HelmetIcon from '@/assets/HemletIcon.vue'
  import OptionIcon from '@/assets/OptionIcon.vue'
  import TrainIcon from '@/assets/TrainIcon.vue'
  import TransferIcon from '@/assets/TransferIcon.vue'
  import PlaneIcon from '@/assets/PlaneIcon.vue'
  import LuggageIcon from '@/assets/LuggageIcon.vue'

  // Skeleton  component (replace with your a new one)
  import ReservationsSkeleton from '../ConfirmedReservations/ReservationsSkeleton.vue'
  import OptionList from './OptionList.vue'
  import PriceAndQuoteButton from './PriceAndQuoteButton.vue'

  // Props
  const props = defineProps({
    booking: {
      type: Object as PropType<BookingSummary>,
      required: true,
    },
    hideActionButton: {
      type: Boolean,
      default: false,
    },
  })

  // i18n instance
  const { t } = useI18n()

  // 2) Access the Vuex store & create a local isLoading computed
  const store = useStore()
  const isServicesLoading = computed(() =>
    store.getters['catalog/isLoading']('fetchServices')
  )

  // Use Composable
  const { groupedOptions, fetchServices } = useServiceCategorization(
    props.booking
  )

  // Fetch services on mounted
  onMounted(async () => {
    await fetchServices()
  })

  const showQuoteOrCartBlock = computed(() =>
    [BookingStatus.Quote, BookingStatus.CartInProgress].includes(
      props.booking.status
    )
  )

  // Computed for building the OptionList
  const optionList = computed(() => [
    {
      options: groupedOptions.value.accommodation,
      sectionTitle: t('reservations.accommodation'),
      iconComponent: AccommodationIcon,
    },
    {
      options: groupedOptions.value.train,
      sectionTitle: t('reservations.train'),
      iconComponent: TrainIcon,
    },
    {
      options: groupedOptions.value.flight,
      sectionTitle: t('reservations.flight'),
      iconComponent: PlaneIcon,
    },
    {
      options: groupedOptions.value.transfert,
      sectionTitle: t('reservations.transfer'),
      iconComponent: TransferIcon,
    },
    {
      options: groupedOptions.value.luggage,
      sectionTitle: t('reservations.luggage'),
      iconComponent: LuggageIcon,
    },
    {
      options: groupedOptions.value.skipass,
      sectionTitle: t('reservations.skipass'),
      iconComponent: SkipassIcon,
    },
    {
      options: groupedOptions.value.material,
      sectionTitle: t('reservations.equipment'),
      iconComponent: EquipmentIcon,
    },
    {
      options: groupedOptions.value.helmet,
      sectionTitle: t('reservations.helmet'),
      iconComponent: HelmetIcon,
    },
    {
      options: groupedOptions.value.option,
      sectionTitle: t('reservations.option'),
      iconComponent: OptionIcon,
    },
  ])

</script>

<style scoped>
  .reservation-summary {
    border-radius: var(--border-radius-default);
  }

  .total-label {
    text-align: left;
    flex-grow: 1;
  }

  .total-price {
    text-align: right;
  }

  .reserve-button {
    margin-top: 1rem;
    align-self: center; /* Centered below the price row */
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .reserve-button {
      width: 100%; /* Full width button on mobile */
    }
  }

  @media (min-width: 769px) {
    .reserve-button {
      width: 33%; /* Button takes up 33% width on desktop */
      margin-left: auto; /* Move button to the right-hand side */
    }
  }
</style>
